import style from './Analysis.module.less';
import {useOutletContext} from 'react-router-dom';
import {AnalysisContext} from '@pages/front/pages/examCenter/pages/analysis/AnalysisPortal';
import {useRouter, useUserInfo} from '@hooks/hooks';
import {useMemo, useState} from 'react';
import {Button, Card, Input, InputNumber, Modal} from 'antd';
import {exportCourseScoreZip} from '@/service/exam/analysis';
import layer from '@/utils/layer';
import {downloadFileByBlob, downloadFileByUrl} from '@pages/common/importExcel/const/const';
import {instance} from '@/service';
import {courseRecord} from '@/utils/const';

const ExportRecord = () => {
  const router = useRouter();
  const { name, examid } = router.getSearchParams();
  const [exportLoading, setExportLoading] = useState(false);
  const [currentType, setCurrentType] = useState('');
  const { courseDcode, canExportStudentScore, primaryLevel, exportQuestionScore } = useOutletContext<AnalysisContext>();
  const [visible, setVisible] = useState<boolean>(false);
  const [lineList, setLineList] = useState<{rank:string[], line: string[]}>({rank: [''], line: ['']});
  const isSingle = window.location.pathname.includes('single');
  const userInfo = useUserInfo();
  const exportList = useMemo(() => {
    let arr: {name: string; des: string; type: string}[] = [];
    if (canExportStudentScore) {
      if (isSingle) {
        arr.push({
          name: '学生本科目成绩（重新计算）',
          des: '普通学生为卷面分，标记学生分数已根据规则重新计算',
          type: 'singleRecord'
        });
      } else {
        arr = arr.concat([{
          name: '学生所有科目成绩表（原始）',
          des: '学生原始卷面分数',
          type: 'ORIGINAL'
        }, {
          name: '学生所有科目成绩表（重新计算）',
          des: '普通学生为卷面分，标记学生分数已根据规则重新计算',
          type: 'FINAL'
        }]);
        if (userInfo?.userType !== 'TEACHER') {
          arr.push({
            name: '学生0分名单',
            des: '学生全科及单科0分名单',
            type: 'zero'
          });
        }
      }
    }
    if (isSingle) {
      // 等第
      if (primaryLevel) {
        arr.push({
          name: '学生本科目等第',
          des: '根据学生原始卷面分转换',
          type: 'singleRank'
        });
      }
    } else {
      // 小题得分
      if (userInfo?.userType === 'TEACHER' && exportQuestionScore) {
        arr.push({
          name: '学生所有科目小题得分明细',
          des: '学生所有科目小题得分明细',
          type: 'allDetail'
        });
      }
      // 等第
      if (primaryLevel) {
        arr.push({
          name: '学生所有科目等第',
          des: '根据学生原始卷面分转换',
          type: 'allRank'
        });
      }
    }
    if (userInfo?.userType !== 'TEACHER') {
      arr.push({
        name: '名次/分数线分析',
        des: '自定义设置名次（如前100名、前200名）、分数线，导出各校分析',
        type: 'line'
      });
    }
    return arr;
  }, [isSingle, courseDcode]);

  const downLoadFn = (data: Blob, names: string) => {
    const file = new FileReader();
    file.readAsText(data, 'utf-8');
    file.onload = () => {
      try {
        const res = JSON.parse(file.result as string);
        if (res.code && res.code === 'FAILURE') {
          layer.error(res.msg);
        }
      } catch {
        downloadFileByBlob(data, names);
      }
    };
  };

  const exportCourseScore = async () => {
    try {
      setExportLoading(true);
      const { data, msg } = await exportCourseScoreZip(examid as ID);
      if (!data) {
        layer.error(msg);
        return false;
      }
      downloadFileByUrl(data.zipFileUrl, data.zipFileName);
    } finally {
      setExportLoading(false);
    }
  };

  const exportStudentScoreTable = (type: string) => {
    setExportLoading(true);
    instance.get('/teacher/exam/statistic/export/studentscore/whole', {
      params: {
        examId: examid,
        scoreType: type
      },
      responseType: 'blob'
    }).then(({ data }) => {
      downLoadFn(data, decodeURI(name) + `-学生成绩表(${type === 'ORIGINAL' ? '原始' : '重新计算'}).xlsx`);
    }).finally(() => {
      setExportLoading(false);
    });
  };

  const downloadStudentScoreExcel = () => {
    setExportLoading(true);
    instance.get('/teacher/exam/statistic/export/studentscore/single', {
      params: {
        examId: examid,
        coursetypeDcode: courseDcode
      },
      responseType: 'blob'
    }).then(({ data }) => {
      downLoadFn(data, decodeURI(name) + '-' + courseRecord[courseDcode] + '-学生成绩表.xlsx');
    }).finally(() => {
      setExportLoading(false);
    });
  };

  const downloadSingleLevel = () => {
    setExportLoading(true);
    instance.get('/teacher/exam/statistic/export/studentscore/single/level', {
      params: {
        examId: examid,
        coursetypeDcode: courseDcode
      },
      responseType: 'blob'
    }).then(({ data }) => {
      downLoadFn(data, decodeURI(name) + '-' + courseRecord[courseDcode] + '-学生等第.xlsx');
    }).finally(() => {
      setExportLoading(false);
    });
  };


  const downloadAllLevel = (type: string) => {
    setExportLoading(true);
    instance.get(`/teacher/exam/statistic/export/studentscore/${type === 'allRank' ? 'level' : 'zero'}`, {
      params: {
        examId: examid
      },
      responseType: 'blob'
    }).then(({ data }) => {
      downLoadFn(data, decodeURI(name) + `-${type === 'allRank' ? '学生等第' : '学生0分名单'}.xlsx`);
    }).finally(() => {
      setExportLoading(false);
    });
  };

  const exportFn = async (type: string) => {
    setCurrentType(type);
    if (type === 'allDetail') {
      await exportCourseScore();
    } else if (type === 'ORIGINAL' || type === 'FINAL') {
      exportStudentScoreTable(type);
    } else if (type === 'singleRecord') {
      downloadStudentScoreExcel();
    } else if (type === 'allRank' || type === 'zero') {
      downloadAllLevel(type);
    } else if (type === 'singleRank') {
      downloadSingleLevel();
    } else if (type === 'line') {
      setVisible(true);
    }
  };

  const inputChange = (val: string, i: number, type: 'rank' | 'line') => {
    let value = '';
    if (type === 'rank') {
      value = val.replace(/\D/g, '');
      if (value) {
        value = value.replace(/^0+/, '') || '0';
      }
    } else {
      value = val.replace(/[^\d.]/g,'');
      value = value.replace(/^\./g,'');
      value = value.replace(/^[0][0]/g,'0');
      value = value.replace(/\.{2,}/g,'.');
      value = value.replace('.','$#$').replace(/\./g,'').replace('$#$','.');
      value = value.replace(/^(-)*(\d+)\.(\d).*$/,'$1$2.5');
    }
    setLineList((origin) => {
      const data = JSON.parse(JSON.stringify(origin));
      data[type][i] = value;
      return data;
    });
  };

  const plusMinus = (opr: string, i: number, type: 'rank' | 'line')=> {
    setLineList((origin) => {
      const data = JSON.parse(JSON.stringify(origin));
      if (opr === 'plus') {
        data[type].push('');
      } else {
        data[type].splice(i, 1);
      }
      return data;
    });
  };

  const onCancel = () => {
    setLineList({rank: [''], line: ['']});
    setVisible(false);
  };

  const onOk = () => {
    setExportLoading(true);
    instance.get(`/edu/mana/statistic/export/analysis/${isSingle? 'single' : 'whole'}`, {
      params: {
        examId: examid,
        courseType: isSingle ? courseDcode : undefined,
        rankList: lineList.rank.join(','),
        scoreList: lineList.line.join(',')
      },
      responseType: 'blob'
    }).then(({ data }) => {
      downLoadFn(data, decodeURI(name) + `-名次分数线分析（${isSingle ? courseRecord[courseDcode] : '全科'}）.xlsx`);
      onCancel();
    }).finally(() => {
      setExportLoading(false);
    });
  };

  return (<>
    <div className={style.exportWrapper}>
      <div className={style.title}>导出报表</div>
      <Card>
        <div className={style.content}>
          {
            exportList.map(al => {
              return <div key={al.type} className={style.exportButton}>
                <div className={style.left}>
                  <img src={require('@/assets/imgs/examCenter/export.png')} />
                  <div>
                    <p>{al.name}</p>
                    <p>{al.des}</p>
                  </div>
                </div>
                <Button type='primary' onClick={() => exportFn(al.type)} loading={exportLoading && currentType === al.type}>导出</Button>
              </div>;
            })
          }
        </div>
      </Card>
    </div>
    <Modal
      centered
      title='名次/分数线设置'
      visible={visible}
      width="550px"
      onCancel={onCancel}
      onOk={onOk}
      confirmLoading={exportLoading}
      okButtonProps={{disabled: !(lineList.rank.find(el => el!=='') || lineList.line.find(el => el!=='')) }}
    >
      <div className={style.lineModal}>
        <div className={style.rowTitle}>{isSingle ? courseRecord[courseDcode] : '总分'}排名：</div>
        <div className={style.row}>
          {lineList.rank.map((el,i) => {
            return <>
              <div className={style.flex}>前
                <Input style={{width: '90px'}} placeholder='请输入' value={el} onChange={e => inputChange(e.target.value, i, 'rank')}/>名
                {((i === 0 && lineList.rank.length > 1) || i > 0) && <div className={style.plusMinus} onClick={() => plusMinus('minus', i, 'rank')}>-</div> }
                {i === lineList.rank.length - 1 && <div className={style.plusMinus} onClick={() => plusMinus('plus', i, 'rank')}>+</div> }
              </div>
            </>;
          })}
        </div>
      </div>
      <div className={style.lineModal}>
        <div className={style.rowTitle}>分数线：</div>
        <div className={style.row}>
          {lineList.line.map((el,i) => {
            return <>
              <div className={style.flex}>
                <Input style={{width: '90px'}} placeholder='请输入' value={el} onChange={e => inputChange(e.target.value, i, 'line')}/>分以上
                {((i === 0 && lineList.line.length > 1) || i > 0) && <div className={style.plusMinus} onClick={() => plusMinus('minus', i, 'line')}>-</div> }
                {i === lineList.line.length - 1 && <div className={style.plusMinus} onClick={() => plusMinus('plus', i, 'line')}>+</div> }
              </div>
            </>;
          })}
        </div>
      </div>
    </Modal>

  </>);
};
export default ExportRecord;
